import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, tap, take } from 'rxjs/operators';
import { AuthApiService } from '@core/services/api/auth.api.service';
import { ProfileApiService } from '@core/services/api/profile.api.service';
import { SubscriptionApi } from '@core/services/api/subscription.api';
import { concat, defer, of } from 'rxjs';
import { Router } from '@angular/router';
import { PROFILE } from '@core/constants/routes.constants';
import { ProfileActions } from './profile.actions';
import { Store } from '@ngrx/store';
import { selectUser } from './profile.selectors';
import { User } from '@core/models/entities/user.models';
import { UserSubscription } from '@core/models/entities/subscription.model';
import { SnackbarApiService } from '@core/services/helpers/snackbar-api.service';

@Injectable()
export class ProfileEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private authApiService: AuthApiService,
    private profileApiService: ProfileApiService,
    private subscriptionApi: SubscriptionApi,
    private snackbarApiService: SnackbarApiService
  ) {}

  initializeProfile$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(ProfileActions.initializeProfile),
        switchMap(() => concat(of(ProfileActions.checkUser()), of(ProfileActions.getSubscription())))
      )
    );
  });

  checkUser$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(ProfileActions.checkUser),
        switchMap(() =>
          this.store.select(selectUser).pipe(
            take(1),
            switchMap(res => {
              if (res.user) {
                return of();
              } else {
                return this.profileApiService.getUser().pipe(
                  map(user => ProfileActions.saveUser({ user })),
                  catchError(error => of(ProfileActions.error({ error: error.message })))
                );
              }
            })
          )
        )
      )
    );
  });

  patchUser$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(ProfileActions.patchUser),
        switchMap(payload =>
          this.profileApiService.patchUser(payload).pipe(
            switchMap((res: User) =>
              concat(of(ProfileActions.saveUser({ user: res })), of(ProfileActions.saveUserSuccess()))
            ),
            catchError(error => of(ProfileActions.error({ error: error.message })))
          )
        ),
        catchError(error => of(ProfileActions.error({ error: error.message })))
      )
    );
  });

  deleteUser$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(ProfileActions.deleteUser),
        switchMap(() => this.profileApiService.deleteUser().pipe(map(() => ProfileActions.navigateToIntro()))),
        catchError(error => of(ProfileActions.error({ error: error.message })))
      )
    );
  });

  getSubscription$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(ProfileActions.getSubscription),
        switchMap(() =>
          this.subscriptionApi.get().pipe(
            map((res: UserSubscription[]) => ProfileActions.getSubscriptionSuccess({ subscriptions: res })),
            catchError(error => of(ProfileActions.error({ error: error.message })))
          )
        ),
        catchError(error => of(ProfileActions.error({ error: error.message })))
      )
    );
  });

  cancelSubscription$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(ProfileActions.cancelSubscription),
        switchMap(payload =>
          this.subscriptionApi.cancel(payload).pipe(
            map(res => ProfileActions.cancelSubscriptionSuccess({ sub: res })),
            catchError(error => of(ProfileActions.cancelSubscriptionFailure({ error: error.message })))
          )
        ),
        catchError(error => of(ProfileActions.error({ error: error.message })))
      )
    );
  });

  cancelSubscriptionSuccess$ = createEffect(
    () => {
      return defer(() =>
        this.actions$.pipe(
          ofType(ProfileActions.cancelSubscriptionSuccess),
          tap(() => this.snackbarApiService.success('The subscription was cancelled.'))
        )
      );
    },
    { dispatch: false }
  );

  cancelSubscriptionFailure$ = createEffect(
    () => {
      return defer(() =>
        this.actions$.pipe(
          ofType(ProfileActions.cancelSubscriptionFailure),
          tap(res => this.snackbarApiService.failure(res.error))
        )
      );
    },
    { dispatch: false }
  );

  saveUserSuccess$ = createEffect(
    () => {
      return defer(() =>
        this.actions$.pipe(
          ofType(ProfileActions.saveUserSuccess),
          tap(() => {
            this.router.navigate([`${PROFILE.root}/${PROFILE.children.view}`], {
              queryParams: { showPopup: true },
            });
          })
        )
      );
    },
    { dispatch: false }
  );

  navigateToView$ = createEffect(
    () => {
      return defer(() =>
        this.actions$.pipe(
          ofType(ProfileActions.navigateToView),
          tap(() => {
            this.router.navigate([`${PROFILE.root}/${PROFILE.children.view}`]);
          })
        )
      );
    },
    { dispatch: false }
  );

  navigateToIntro$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(ProfileActions.navigateToIntro),
        switchMap(() =>
          this.authApiService.logOut('/auth/intro').pipe(
            map(() => ProfileActions.clearUser()),
            catchError(error => of(ProfileActions.error({ error })))
          )
        )
      )
    );
  });
}
